import React, { useEffect, useState } from 'react';
import PerinHealthGrid from '../../components/PerinHealthGrid';
import { setSelectedRows, setSelectedItem, setSelectAll } from '../../components/PerinHealthGridSlice';
import { resetFilterState } from '../../slicers/FilterSlice';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import useAxios from '../../services/AxiosConfig';
import FilterManagementView from '../../components/FilterManagementView';
import { closeModal } from '../../components/PerinHealthModelSlice';
import { showAlert } from '../../components/PerinHealthAlertSlice';
import managementStyles from '../ManagementPages.module.css';
import { setUpdatingDevice, resetDeviceState, setAssignmentData, setAllGroupDevices } from './DeviceSlice';
import { openModal } from '../../components/PerinHealthModelSlice';

const DeviceManagement = () => {

    const dispatch = useDispatch();
    const axiosConfig = useSelector((state) => state.axiosConfig);
    const httpService = useAxios(axiosConfig.envURL);
    useEffect(() => {
        dispatch(resetFilterState());
        dispatch(setSelectedRows([]));
        dispatch(setSelectedItem({}));
        dispatch(resetDeviceState());
    }, []);
    const navigate = useNavigate();
    const perinHealthGrid = useSelector((state) => state.perinHealthGrid);
    const userId = useSelector((state) => state.userInfo.id);
    const [activeDeviceIds, setActiveDeviceIds] = useState([]);
    const [activeTab, setActiveTab] = useState('wearable');

    const headers = [
        { key: 'id', label: "ID"},
        { key: 'manufacturer', label: "Device"},
        { key: 'model', label: "Model"},
        { key: 'quantity', label: "Quantity"},
        { key: 'assigned', label: "Assigned"},
        { key: 'groupName', label: 'Group'}
    ];
    
    const [deviceData, setDeviceData] = useState([]);
    const [allDeviceData, setAllDeviceData] = useState([]);

    useEffect(() => {
        getDevices();
        getCarePrograms();
    }, []);

    // Filter devices based on active tab
    useEffect(() => {
        if (activeTab === 'wearable') {
            // Filter for wearable devices (deviceId === 1 is Health Patch/Perin Patch)
            const wearableDevices = allDeviceData.filter(device => device.deviceId === 1);
            setDeviceData(wearableDevices);
        } else {
            // Filter for other devices (deviceId !== 1)
            const otherDevices = allDeviceData.filter(device => device.deviceId !== 1);
            setDeviceData(otherDevices);
        }
    }, [activeTab, allDeviceData]);

    const getDevices = async () => {
        httpService.get(`device/group/${userId}`)
            .then((responseData) => {
                if (Array.isArray(responseData.data.data)) {
                    let response = [...responseData.data.data];
                    for (const device of response) {
                        const numAssigned = device.serialNumbers.filter((item)=>item.isAssigned).length
                        device.assigned = numAssigned;
                      }
                    dispatch(setAllGroupDevices(response));
                    setAllDeviceData(response);
                } else {
                    console.error('Invalid data format:');
                }
            })
            .catch((err) => {
                console.log(err);
            });
    };

    const getCarePrograms = () => {
        httpService.get(`careprogram/list/${userId}`)
        .then((responseData) => {
            if (Array.isArray(responseData.data.data)) {
                const uniqueDeviceIds = new Set();
                for (const program of responseData.data.data) {
                    for (const device of program.devices) {
                        uniqueDeviceIds.add(device.deviceId);
                    }
                }
                setActiveDeviceIds(Array.from(uniqueDeviceIds));
            } else {
                setActiveDeviceIds([]);
            }
        })
        .catch((err) => {
            console.log(err);
            setActiveDeviceIds([]);
        });
    };

    const onAddDeviceHandler = () => {
        navigate('/add-device');
    }

    const onRowClickHandler = (rowItem) => {
        dispatch(setUpdatingDevice(rowItem));
        navigate(`/add-device`);
    };

    const onRowValueClickHandler = (item) => {
        dispatch(setAssignmentData({
            "groupName":item.groupName, 
            "totalDevices":item.quantity,
            "deviceName":item.model,
            "groupDeviceId": item.id,
            "serialNums": item.serialNumbers
        }))
        navigate('/device-group-assignment');
    };

    const showDeleteModal = () => {
        const devicesToDelete = [];
        const devicesCannotDelete = [];
        
        perinHealthGrid.selectedRows.forEach(device => {
            if (activeDeviceIds.includes(device.id) || device.assigned >= 1) {
                devicesCannotDelete.push(device);
            } else {
                devicesToDelete.push(device);
            }
        });

        const cannotDeleteNames = devicesCannotDelete.map(device => `${device.manufacturer} - ${device.model}`).join('\n');
        const canDeleteNames = devicesToDelete.map(device => `${device.manufacturer} - ${device.model}`).join('\n');

        if (devicesCannotDelete.length > 0 && devicesToDelete.length === 0) {
            dispatch(
                openModal({
                    activeModalId: "FilterManagementView",
                    header: 'Cannot Delete Devices',
                    message: `The following devices cannot be deleted because they are assigned to a care program or have active serial number assignments:\n\n<i>${cannotDeleteNames}</i>`,
                    buttons: [
                        { label: 'Ok', variant: 'primary', onClickKey: 'OK' },
                    ],
                })
            );
            return;
        } else if (devicesCannotDelete.length > 0 && devicesToDelete.length > 0) {
            dispatch(
                openModal({
                    activeModalId: "FilterManagementView",
                    header: 'Partial Delete Warning',
                    message: `The following devices <strong>will</strong> be deleted:\n<i>${canDeleteNames}</i>\n\nThe following devices <strong>cannot</strong> be deleted because they are currently assigned to care programs or have active serial number assignments:\n<i>${cannotDeleteNames}</i>`,
                    buttons: [
                       
                        { label: 'Cancel', variant: 'secondary', onClickKey: 'NO' },
                        { label: 'Confirm', variant: 'primary', onClickKey: 'YES' },
                    ],
                })
            );
        } else {
            dispatch(
                openModal({
                    activeModalId: "FilterManagementView",
                    header: 'Delete Devices?',
                    message: `The following devices will be deleted:\n<i>${canDeleteNames}</i>`,
                    buttons: [
                       
                        { label: 'Cancel', variant: 'secondary', onClickKey: 'NO' },
                        { label: 'Confirm', variant: 'primary', onClickKey: 'YES' },
                    ],
                })
            );
        }
    };

    const onDeleteHandler = () => {
        const devicesToDelete = [];
        
        perinHealthGrid.selectedRows.forEach(device => {
            if (!activeDeviceIds.includes(device.id) && device.assigned == 0) {
                devicesToDelete.push(device);
            } 
        });

        const deletePromises = devicesToDelete.map((device) => {
            const id = device.id;
            return httpService.delete(`device/group/${id}`)
                .then(() => ({ success: true, device }))
                .catch((error) => ({ success: false, device, error }));
        });

        Promise.allSettled(deletePromises)
            .then((results) => {
                const successful = [];
                const failed = [];

                results.forEach((result) => {
                    if (result.status === 'fulfilled') {
                        const { success, device, error } = result.value;
                        if (success) {
                            successful.push(device);
                        } else {
                            failed.push({ device, error });
                        }
                    } else {
                        failed.push({ device: null, error: result.reason });
                    }
                });
                dispatch(setSelectedRows([]));
                dispatch(setSelectAll(false));
                getDevices();
                dispatch(closeModal());

                if (successful.length > 0 && failed.length === 0) {
                    // All successful
                    dispatch(
                        showAlert({
                            header: "Success",
                            message: `Successfully deleted ${successful.length} device${successful.length > 1 ? 's' : ''}.`,
                            type: 'success'
                        })
                    );
                } else if (successful.length > 0 && failed.length > 0) {
                    // Partial success
                    dispatch(
                        showAlert({
                            header: "Partial Success",
                            message: `Successfully deleted ${successful.length} device${successful.length > 1 ? 's' : ''}, but failed to delete ${failed.length} device${failed.length > 1 ? 's' : ''}.`,
                            type: 'warning'
                        })
                    );
                } else {
                    // All failed
                    dispatch(
                        showAlert({
                            header: "Error",
                            message: `Failed to delete ${failed.length} device${failed.length > 1 ? 's' : ''}.`,
                            type: 'danger'
                        })
                    );
                }

                // Log detailed errors for debugging
                if (failed.length > 0) {
                    console.error('Failed deletions:', failed);
                }
            });
    };

    // Tab styling function
    const tabStyle = (isActive) => ({
        paddingLeft: 20,
        paddingRight: 20,
        paddingTop: 4,
        paddingBottom: 4,
        background: isActive ? 'rgba(203, 243, 210, 0.30)' : '#F3F3F3',
        borderBottom: isActive ? '3px #006969 solid' : '0.50px #B9B9B9 solid',
        cursor: 'pointer'
    });

    return (
        <div className={managementStyles.MiddleContent}>
            <FilterManagementView
                isOrganization={true}
                isGroup={true}
                isSubGroup={false}
                isTeam={false}
                addButtonLabel='Add Device'
                onAddHandler={onAddDeviceHandler}
                onDeleteHandler={onDeleteHandler}
                modelHeader='Delete Device?'
                customDeleteModal={showDeleteModal}
            />
            
            {/* Tab Navigation */}
            <div style={{width: '100%', borderBottom: '0.50px #B9B9B9 solid', marginBottom: '20px'}}>
                <div style={{height: 32, display: 'flex', justifyContent: 'flex-start'}}>
                    <div 
                        style={tabStyle(activeTab === 'wearable')}
                        onClick={() => setActiveTab('wearable')}
                    >
                        <span style={{
                            color: activeTab === 'wearable' ? 'black' : '#373737',
                            fontSize: 16,
                            fontFamily: 'Roboto',
                            fontWeight: '400',
                            lineHeight: '24px',
                            letterSpacing: 0.04
                        }}>Wearable</span>
                    </div>
                    <div 
                        style={tabStyle(activeTab === 'other')}
                        onClick={() => setActiveTab('other')}
                    >
                        <span style={{
                            color: activeTab === 'other' ? 'black' : '#373737',
                            fontSize: 16,
                            fontFamily: 'Roboto',
                            fontWeight: '400',
                            lineHeight: '24px',
                            letterSpacing: 0.04
                        }}>Other</span>
                    </div>
                </div>
            </div>

            <div className={managementStyles.GridContainer}>
            <PerinHealthGrid
                dataList={deviceData}
                headers={headers}
                showCheckbox={true}
                showEditIcon={true}
                onRowClick={onRowClickHandler}
                onRowValueClick={onRowValueClickHandler}
                showHeaderFilter={true}
                gridHeight="100%"
                rowValueAction={'manufacturer'} 
            />
            </div>
        </div>
    )
}

export default DeviceManagement;
